import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "co_pwa_prompt_dismissed_at";
const DISMISS_DAYS = 14; // hide for 2 weeks after user closes

function wasRecentlyDismissed() {
  const t = localStorage.getItem(DISMISS_KEY);
  if (!t) return false;
  return Date.now() - Number(t) < DISMISS_DAYS * 86400000;
}

function isRunningStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Shows a discreet "Install CollectOne" prompt for supported browsers:
 * - Chromium desktop/Android: uses the native beforeinstallprompt event.
 * - iOS Safari: no programmatic install, so we show a helper hint
 *   ("Tap Share → Add to Home Screen") only when it makes sense.
 */
export default function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isRunningStandalone() || wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari fallback
    const ua = window.navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      setVisible(false);
    } else {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setVisible(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-40 bg-white border co-hairline shadow-lg rounded-sm p-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 shrink-0 bg-[#ECECFE] text-[#4338CA] grid place-items-center rounded-sm">
        <Download className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm text-[#0A0A0B] leading-tight">
          Install CollectOne
        </div>
        <div className="text-xs text-[#52525B] mt-1 leading-relaxed">
          {iosHint ? (
            <>
              Tap the <span className="font-semibold">Share</span> icon in Safari,
              then <span className="font-semibold">&quot;Add to Home Screen&quot;</span> to
              launch CollectOne like a native app.
            </>
          ) : (
            <>Get a native-like app icon on your home screen. Works offline-friendly and launches faster.</>
          )}
        </div>
        {!iosHint && (
          <button
            data-testid="pwa-install-button"
            onClick={install}
            className="mt-3 inline-flex items-center gap-1.5 bg-[#4338CA] hover:bg-[#3730A3] text-white px-3 py-1.5 text-xs font-semibold rounded-sm co-btn-press"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={2} />
            Install app
          </button>
        )}
      </div>
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="p-1 -m-1 text-[#71717A] hover:text-[#0A0A0B] rounded-sm"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  );
}
